import type { MouseEvent } from "react";
import { useEffect, useState } from "react";
import type { Role, User } from "../api";
import { api } from "../api";

type Props = {
  token: string;
  user: User;
  onClose: () => void;
  onSaved?: () => void;
};

export function UserRolesModal({ token, user, onClose, onSaved }: Props) {
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([api.listRoles(token), api.getUserRoles(token, user.id)])
      .then(([all, mine]) => {
        if (cancelled) return;
        setRoles(all);
        setSelected(new Set(mine.map((r) => r.id)));
      })
      .catch((e: { message?: string }) => {
        if (!cancelled) setError(e?.message ?? String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, user.id]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onBackdrop = (ev: MouseEvent<HTMLDivElement>) => {
    if (ev.target === ev.currentTarget && !saving) onClose();
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.putUserRoles(token, user.id, [...selected]);
      onSaved?.();
      onClose();
    } catch (e: unknown) {
      const msg = typeof e === "object" && e && "message" in e ? String((e as { message?: string }).message) : String(e);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onBackdrop}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg border border-slate-200 shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col"
        role="dialog"
        aria-labelledby="user-roles-modal-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200 flex justify-between gap-4">
          <div>
            <h3 id="user-roles-modal-title" className="text-base font-semibold text-slate-900">
              Assign roles
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              User <span className="font-mono">{user.username}</span> (id {user.id}). Schema access is the union of the
              roles selected here.
            </p>
          </div>
          <button type="button" onClick={onClose} className="btn shrink-0" aria-label="Close">
            Close
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto space-y-3">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
          )}
          {loading || roles === null ? (
            <p className="text-sm text-slate-500">Loading roles…</p>
          ) : roles.length === 0 ? (
            <p className="text-sm text-slate-600">No roles yet. Create one in the Roles tab first.</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded border border-slate-200">
              {roles.map((r) => (
                <li key={r.id} className="flex items-start gap-3 px-3 py-2">
                  <input
                    id={`role-${r.id}`}
                    type="checkbox"
                    className="mt-1"
                    checked={selected.has(r.id)}
                    onChange={() => toggle(r.id)}
                  />
                  <label htmlFor={`role-${r.id}`} className="min-w-0 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{r.name}</span>
                      {r.confers_admin && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                          ★ admin
                        </span>
                      )}
                      <span className="text-[11px] text-slate-400">{r.grant_count} grant(s)</span>
                    </div>
                    {r.description && <div className="text-xs text-slate-500">{r.description}</div>}
                  </label>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-slate-500">
            Membership in an <strong>admin</strong> role grants full portal administration. Removing the last admin or
            the break-glass user's admin role is blocked server-side.
          </p>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50 rounded-b-lg">
          <button type="button" className="btn" disabled={saving} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" disabled={saving || loading} onClick={() => void onSave()}>
            {saving ? "Saving…" : "Save roles"}
          </button>
        </div>
      </div>
    </div>
  );
}
